import type { ReactNode } from 'react'
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useMemo,
    useCallback,
    useRef,
} from 'react'
import { useAuthUser, useAuthStatus } from './AuthContext'
import { getBrowserAdapter, type BrowserAdapter } from '../lib/browserAdapter'
import { browserScheduler } from '../lib/scheduler'

// Local type definition for LLM key status
interface LLMKeyStatus {
    exists: boolean
    last4?: string
    updated_at?: string
}

interface LLMKeyContextType {
    keyStatus: LLMKeyStatus | null
    loading: boolean
    error: string | null
    refreshKeyStatus: () => Promise<void>
    hasLLMKey: boolean
    forceRefresh: () => void
}

// Justified exception: Context export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: context export
export const LLMKeyContext = createContext<LLMKeyContextType | undefined>(undefined)

// Justified exception: Custom hook export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: custom hook export
export const useLLMKey = () => {
    const context = useContext(LLMKeyContext)
    if (context === undefined) {
        throw new Error('useLLMKey must be used within an LLMKeyProvider')
    }
    return context
}

interface LLMKeyProviderProps {
    children: ReactNode
    /** Initial key status for testing (bypasses fetch) */
    initialKeyStatus?: LLMKeyStatus | null
    /** Skip initial fetch (useful for testing) */
    skipFetch?: boolean
    /** Initial loading state for Storybook stories. Defaults to `!skipFetch`. */
    initialLoading?: boolean
    /** Optional browser adapter for testability. Defaults to browserAdapter. */
    browserAdapter?: BrowserAdapter
}

export const LLMKeyProvider: React.FC<LLMKeyProviderProps> = ({
    children,
    initialKeyStatus,
    skipFetch = false,
    initialLoading,
    browserAdapter: browserAdapterProp = getBrowserAdapter(),
}) => {
    const [keyStatus, setKeyStatus] = useState<LLMKeyStatus | null>(initialKeyStatus ?? null)
    const [loading, setLoading] = useState(initialLoading ?? !skipFetch)
    const [error, setError] = useState<string | null>(null)
    const user = useAuthUser()
    const { status } = useAuthStatus()

    // Use refs to store latest values for stable callback, but track actual changes for effects
    const userRef = useRef(user)
    const statusRef = useRef(status)

    // Update refs when actual values change
    if (userRef.current !== user) {
        userRef.current = user
    }
    if (statusRef.current !== status) {
        statusRef.current = status
    }

    // Extract stable identifiers for dependency tracking
    const userId = user?.id
    const isSignedIn = status === 'signedIn'

    // Stabilize refreshKeyStatus callback - use refs to avoid recreation on object reference changes
    // Store callback in ref to break dependency loop
    const refreshKeyStatusRef = useRef<(() => Promise<void>) | undefined>(undefined)

    const refreshKeyStatus = React.useCallback(async () => {
        const currentUser = userRef.current
        const currentStatus = statusRef.current

        if (!currentUser || currentStatus !== 'signedIn') {
            setKeyStatus(null)
            setLoading(false)
            setError(null)
            return
        }

        // Platform tokens are managed separately by admins.
        // Platform token is managed at the admin level and is always available if configured.
        setLoading(true)
        setError(null)
        // Simulate async operation for consistency with existing behavior
        await new Promise<void>((resolve) => {
            browserScheduler.setTimeout(() => {
                resolve()
            }, 0)
        })
        setKeyStatus({ exists: true })
        setLoading(false)
    }, []) // Empty deps - uses refs for user and status, so callback doesn't need to be recreated

    // Update ref when callback changes
    refreshKeyStatusRef.current = refreshKeyStatus

    // Refresh key status when user ID or status changes (unless skipFetch is true)
    // Use ref to avoid including callback in dependencies (which would cause loops)
    useEffect(() => {
        if (!skipFetch) {
            void refreshKeyStatusRef.current?.()
        }
    }, [userId, isSignedIn, skipFetch]) // Don't include refreshKeyStatus to avoid loops

    // Listen for custom events (no-op since platform tokens are managed separately)
    useEffect(() => {
        const handleKeyChange = () => {
            // No-op: platform tokens are managed separately by admins
        }

        browserAdapterProp.addEventListener('llm-key-changed', handleKeyChange)
        return () => browserAdapterProp.removeEventListener('llm-key-changed', handleKeyChange)
    }, [browserAdapterProp]) // Only depend on browserAdapter, use ref for callback

    const hasLLMKey = keyStatus?.exists === true

    // Stabilize forceRefresh to use ref
    const forceRefresh = useCallback(() => {
        void refreshKeyStatusRef.current?.()
    }, []) // Empty deps - uses ref

    // Stabilize context value - split into stable callbacks and dynamic state
    const stableCallbacks = useMemo(
        () => ({
            refreshKeyStatus,
            forceRefresh,
        }),
        [refreshKeyStatus, forceRefresh]
    )

    const value = useMemo<LLMKeyContextType>(
        () => ({
            keyStatus,
            loading,
            error,
            refreshKeyStatus: stableCallbacks.refreshKeyStatus,
            hasLLMKey,
            forceRefresh: stableCallbacks.forceRefresh,
        }),
        [keyStatus, loading, error, stableCallbacks, hasLLMKey]
    )

    return <LLMKeyContext.Provider value={value}>{children}</LLMKeyContext.Provider>
}
